import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  input,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  type MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FeatureFlagsService } from '@principle-theorem/ng-feature-flags';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  ISelectOptionGroup,
  OptionGroupSearchFilter,
  SearchFieldComponent,
  TrackByFunctions,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import {
  ServiceProvider,
  ServiceProviderHandler,
} from '@principle-theorem/principle-core';
import { SERVICE_PROVIDER_FEATURE_MAP } from '@principle-theorem/principle-core/features';
import {
  type IServiceCode,
  type IServiceCodeEntry,
  type ServiceCodeType,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  multiSwitchMap,
  reduce2DArray,
} from '@principle-theorem/shared';
import { compact, sortBy, uniq } from 'lodash';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'pr-add-service-code',
  templateUrl: './add-service-code.component.html',
  styleUrls: ['./add-service-code.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatAutocompleteModule,
    MatInputModule,
    MatFormFieldModule,
    ScrollingModule,
    ReactiveFormsModule,
    SearchFieldComponent,
  ],
})
export class AddServiceCodeComponent {
  trackByOption = TrackByFunctions.field<IServiceCode>('code');
  trackByGroup =
    TrackByFunctions.field<ISelectOptionGroup<IServiceCode>>('name');
  search: OptionGroupSearchFilter<IServiceCode>;
  searchCtrl = new TypedFormControl<string>('');
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @Output() codeSelected = new EventEmitter<IServiceCode>();
  commonCodes$ = new BehaviorSubject<IServiceCodeEntry[]>([]);
  serviceCodeType = input<ServiceCodeType>();
  slim = input(false);

  @Input()
  set commonCodes(commonCodes: IServiceCodeEntry[]) {
    if (commonCodes) {
      this.commonCodes$.next(commonCodes);
    }
  }

  constructor(org: OrganisationService, featureFlags: FeatureFlagsService) {
    const commonCodes$ = this.commonCodes$.pipe(
      map((commonCodes) => [
        {
          name: 'Common Codes',
          options: compact(
            commonCodes.map((commonCode) => {
              const code = ServiceProviderHandler.resolveServiceCode(
                commonCode.type,
                commonCode.code
              );
              if (!code) {
                return;
              }
              return {
                ...code,
                type: commonCode.type,
              };
            })
          ).filter((commonCode) => !commonCode.deleted),
          skipFilter: false,
        },
      ])
    );

    const enabledProviderLabels$ = of(
      Object.values(SERVICE_PROVIDER_FEATURE_MAP)
    ).pipe(
      multiSwitchMap((label) =>
        featureFlags
          .isFeatureEnabledByLabel$(label)
          .pipe(map((enabled) => (enabled ? label : undefined)))
      ),
      map((labels) => compact(uniq(labels)))
    );

    const serviceCodes$ = combineLatest([
      org.organisation$.pipe(filterUndefined()),
      enabledProviderLabels$,
      toObservable(this.serviceCodeType),
    ]).pipe(
      map(([organisation, enabledLabels, serviceCodeType]) =>
        ServiceProvider.providersByRegion(organisation.region).filter(
          (provider) => {
            if (serviceCodeType && provider.type !== serviceCodeType) {
              return false;
            }
            const featureLabel = SERVICE_PROVIDER_FEATURE_MAP[provider.type];
            return !featureLabel || enabledLabels.includes(featureLabel);
          }
        )
      ),
      map((serviceProviders) =>
        serviceProviders.map((serviceProvider) => ({
          name: `${serviceProvider.label} Codes`,
          options: sortBy(Object.values(serviceProvider.items), 'code')
            .filter((code) => !code.deleted)
            .map((code) => ({
              ...code,
              type: serviceProvider.type,
            })),
          skipFilter: false,
        }))
      )
    );

    this.search = new OptionGroupSearchFilter<IServiceCode>(
      combineLatest([commonCodes$, serviceCodes$]).pipe(reduce2DArray()),
      this.searchCtrl.valueChanges.pipe(startWith('')),
      ['code', 'title']
    );
  }

  displayFn(code?: IServiceCode): string {
    return code ? `${code.code} - ${code.title}` : '';
  }

  optionSelected(event: MatAutocompleteSelectedEvent): void {
    if (!event.option.value) {
      return;
    }
    this.codeSelected.emit(event.option.value as IServiceCode);
    this.searchCtrl.reset('');
  }

  focusInput(): void {
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.focus();
    }
  }
}
